/**
 * Hospital Indemnity Benefit Definitions
 *
 * Defines all benefit fields for Hospital Indemnity coverage using builder functions.
 * Enum values provided via typeEnumValues parameter using OpenAPI types.
 */

import {
  BaseBenefitPlanPolicyPeriod,
  extendEnumWithNotDisclosed,
  extendEnumWithNotDisclosedAndNotOffered,
  NotDisclosed,
  RateGuaranteeTypeEnum,
  RfpInclusionStatusEnum,
  RfpVisionCopayRuleType,
} from "@platform/openapi-types";

import { mpOnlyRow } from "../shared/definitions/mpOnlyRow";
import { BenefitDefinitions } from "../../benefits/benefit.types";
import {
  annualOpenEnrollmentType,
  currentCarrierName,
  dollarAmount,
  eligibilityHoursDropdown,
  enumDropdown,
  eligibilityClasses,
  pregnancyDropdown,
  text,
  numberField,
} from "../shared/cellSpecs/common";
import {
  buildPreExistingConditionBenefitPair,
  buildTypeAmountBenefitPair,
  buildCommissionBenefits,
} from "../shared/definitions/builders";

// Pre-build commission definitions to override isRequired on type fields
const commissionDefs = buildCommissionBenefits();

export const benefits: BenefitDefinitions = {
  currentCarrierName: {
    label: "Current Carrier",
    description: "Name of the current insurance carrier",
    cell: currentCarrierName,
    isRequired: true,
    networkDependent: false,
    benefitPlanLevel: true,
    editableWhen: {
      dependsOn: "policyPeriod",
      disabledValues: [
        BaseBenefitPlanPolicyPeriod.newBusiness,
        BaseBenefitPlanPolicyPeriod.alternate,
      ],
    },
  },
  ...commissionDefs,
  currentCommissionType: {
    ...commissionDefs["currentCommissionType"],
    isRequired: true,
    editableWhen: {
      dependsOn: "policyPeriod",
      disabledValues: [
        BaseBenefitPlanPolicyPeriod.newBusiness,
        BaseBenefitPlanPolicyPeriod.alternate,
      ],
    },
  },
  requestedCommissionType: {
    ...commissionDefs["requestedCommissionType"],
    isRequired: true,
  },
  eligibilityClasses: {
    label: "Eligibility Class",
    description: "Benefit classes assigned to this benefit plan group",
    cell: eligibilityClasses,
    benefitPlanLevel: true,
    isRequired: true,
  },
  twentyFourHourCoverage: {
    mpOnly: true,
    label: "24-hour Coverage",
    mpConfigKey: "24_hour_coverage",
    description: "24-hour coverage details",
    cell: {
      ...enumDropdown,
      cellEditorParams: {
        ...enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(RfpInclusionStatusEnum),
      },
    },
    networkDependent: false,
  },
  ...buildTypeAmountBenefitPair({
    prefix: "hospitalAdmission",
    isRequired: true,
    typeLabel: "Hospital Admission",
    typeCell: enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(RfpVisionCopayRuleType),
    amountCell: dollarAmount,
    typeDescription: "Type of benefit paid upon hospital admission",
    amountDescription: "Hospital admission benefit amount",
    amountDefaultValue: 0,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "icuAdmission",
    isRequired: true,
    typeLabel: "ICU Admission",
    typeCell: enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(RfpVisionCopayRuleType),
    amountCell: dollarAmount,
    typeDescription: "Type of benefit paid upon ICU admission",
    amountDescription: "ICU admission benefit amount",
    amountDefaultValue: 0,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "hospitalConfinement",
    isRequired: true,
    typeLabel: "Hospital Confinement",
    typeCell: enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(RfpVisionCopayRuleType),
    amountCell: dollarAmount,
    typeDescription: "Type of daily benefit for hospital confinement",
    amountDescription: "Hospital confinement daily benefit amount",
    amountDefaultValue: 0,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "icuConfinement",
    isRequired: true,
    typeLabel: "ICU Confinement",
    typeCell: enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(RfpVisionCopayRuleType),
    amountCell: dollarAmount,
    typeDescription: "Type of daily benefit for ICU confinement",
    amountDescription: "ICU confinement daily benefit amount",
    amountDefaultValue: 0,
  }),
  // Extended (non-v5) fields (CD-1943), placed per AC directly below ICU
  // Confinement and above Pre-existing Condition.
  pregnancy: {
    mpOnly: true,
    label: "Pregnancy",
    mpConfigKey: "pregnancy",
    description: "Pregnancy coverage details",
    cell: pregnancyDropdown,
    networkDependent: false,
  },
  newbornNurseryCare: {
    mpOnly: true,
    label: "Newborn/ Nursery Care",
    mpConfigKey: "newborn_nursery_care",
    description: "Newborn/nursery care coverage details",
    cell: {
      ...enumDropdown,
      cellEditorParams: {
        ...enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(RfpInclusionStatusEnum),
      },
    },
    networkDependent: false,
  },
  ...buildPreExistingConditionBenefitPair({
    typeDescription: "Type of pre-existing condition limitation",
    monthsDetailDescription:
      "Configure months diagnosis free before/after effective date and treatment free. Format: XX/XX or XX/XX/XX",
    disabledValues: ["waived", "none", NotDisclosed],
    monthsDetailDefaultValue: "01/01",
  }),
  portability: {
    label: "Portability",
    description: "Portability options",
    cell: {
      ...enumDropdown,
      cellEditorParams: {
        ...enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(RfpInclusionStatusEnum),
      },
    },
    networkDependent: false,
  },
  ...buildTypeAmountBenefitPair({
    prefix: "wellness",
    typeLabel: "Wellness Benefit",
    typeCell: enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(RfpVisionCopayRuleType),
    amountCell: dollarAmount,
    typeDescription: "Type of wellness benefit",
    amountDescription: "Wellness benefit amount",
    amountDefaultValue: 0,
  }),
  annualOpenEnrollment: {
    label: "Annual Open Enrollment",
    mpConfigKey: "annual_open_enrollment",
    description: "Annual open enrollment options",
    cell: annualOpenEnrollmentType,
    networkDependent: false,
  },
  participationRequirements: {
    label: "Participation Requirements",
    description: "Participation requirement details for the benefit plan",
    cell: text,
    networkDependent: false,
    benefitPlanLevel: true,
  },
  ...buildTypeAmountBenefitPair({
    prefix: "rateGuarantee",
    typeLabel: "Rate Guarantee",
    typeCell: enumDropdown,
    typeEnumValues: Object.keys(RateGuaranteeTypeEnum),
    amountCell: numberField,
    typeDescription:
      "Time frame in which rates are guaranteed to stay the same",
    amountDescription: "Number of months or years the rate is guaranteed",
    amountDefaultValue: 1,
    disabledValues: [
      RateGuaranteeTypeEnum.notIncluded,
      RateGuaranteeTypeEnum.notOffered,
    ],
  }),
  volumeIllustratedFrom: mpOnlyRow(
    "Volume illustrated from _",
    "volume_illustrated_from",
  ),
  enrolledLives: mpOnlyRow("Enrolled Lives", "enrolled_lives"),

  // Extended (non-v5) fields (CD-1943/CD-2076). Backend fields are plain
  // strings for now (RFC-004), not yet enums, so these are text rather than
  // dropdowns. (Pregnancy and Newborn/Nursery Care are defined above, in
  // their AC-specified row positions.)
  eligibility: {
    mpOnly: true,
    label: "Eligibility",
    mpConfigKey: "eligibility",
    description: "Eligibility requirements for the benefit plan",
    cell: text,
    networkDependent: false,
  },
  // Unlike its sibling extended fields, eligibilityHours has a confirmed
  // dropdown value list (CD-2325/CD-2076).
  eligibilityHours: {
    mpOnly: true,
    label: "Eligibility Hours",
    mpConfigKey: "eligibility_hours",
    description: "Minimum hours worked per week to be eligible for the plan",
    cell: eligibilityHoursDropdown,
    networkDependent: false,
  },
  pregnancyWaitingPeriodNote: {
    mpOnly: true,
    label:
      "Pregnancy benefits may be subject to a waiting period. Refer to the carrier proposals for details.",
    mpConfigKey: "pregnancy_waiting_period_note",
    description: "Note on pregnancy benefit waiting period",
    cell: text,
    networkDependent: false,
  },
};
